/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import Text from "../ui/Text";
import { ROUTES } from "../../constants/constants";
import ClientLogoutConfirmationModal from "./ClientLogoutConfirmationModal";
import { useAuth } from "../../hooks/useAuth";
import Icon from "../ui/Icon";
import Button from "../ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuHeader,
  DropdownMenuItem,
  DropdownMenuDivider,
} from "../ui/DropdownMenu";
import usePluginUtils from "../../hooks/usePluginUtils";
import { usePackages, usePackageInstances } from "../../lib/react-query/usePackages";

const ClientProfilePopover = () => {
  const navigate = useNavigate();
  const { client } = useAuth();
  const { isPluginEnabled } = usePluginUtils();
  const promotionsPluginEnabled = isPluginEnabled("promotion");
  const packagePluginEnabled = isPluginEnabled("package");
  const membershipPluginEnabled = isPluginEnabled("membership");
  const waitingListPluginEnabled = isPluginEnabled("waiting_list");
  const { data: availablePackages } = usePackages({ enabled: packagePluginEnabled });
  const { data: ownedPackagesData } = usePackageInstances(undefined, { enabled: packagePluginEnabled });
  const hasAvailablePackages = Array.isArray(availablePackages) && availablePackages.length > 0;
  const hasPurchasedPackages = (ownedPackagesData?.pages?.[0]?.data?.length ?? 0) > 0;
  const showPackagesMenuItem = packagePluginEnabled && (hasPurchasedPackages || hasAvailablePackages);

  return (
    <DropdownMenu placement="bottom-right">
      <DropdownMenuTrigger>
        <Button variant="transparent" size="icon" icon={<Icon.User size={Icon.SIZE.SM} />} />
      </DropdownMenuTrigger>
      <DropdownMenuContent minWidth="280px">
        <DropdownMenuHeader>
          {client.name && (
            <Text color="dark" weight="bold">
              {client.name}
            </Text>
          )}
          {client.email && <Text size="small">{client.email}</Text>}
        </DropdownMenuHeader>
        <DropdownMenuItem
          icon={<Icon.Gear />}
          onClick={() => navigate(ROUTES.CLIENT_PROFILE)}
          title="My profile"
        />
        <DropdownMenuItem
          icon={<Icon.CalendarCheck />}
          onClick={() => navigate(ROUTES.CLIENT_BOOKINGS)}
          title="My bookings"
        />
        {promotionsPluginEnabled && (
          <DropdownMenuItem
            icon={<Icon.Gift />}
            onClick={() => navigate(ROUTES.CLIENT_PROMOTIONS)}
            title="My gift cards"
          />
        )}
        {membershipPluginEnabled && (
          <DropdownMenuItem
            icon={<Icon.Membership />}
            onClick={() => navigate(ROUTES.CLIENT_MEMBERSHIPS)}
            title="My memberships"
          />
        )}
        {showPackagesMenuItem && (
          <DropdownMenuItem
            icon={<Icon.BoxArchive />}
            onClick={() => navigate(ROUTES.CLIENT_PACKAGES)}
            title="My packages"
          />
        )}
        {waitingListPluginEnabled && (
          <DropdownMenuItem
            icon={<Icon.ClockCheck />}
            onClick={() => navigate(ROUTES.CLIENT_WAITING_LIST)}
            title="Waiting list"
          />
        )}
        <DropdownMenuItem
          icon={<Icon.CreditCard />}
          onClick={() => navigate(ROUTES.CLIENT_PAYMENTS)}
          title="Payments"
        />
        <DropdownMenuDivider />
        <ClientLogoutConfirmationModal
          trigger={
            <DropdownMenuItem icon={<Icon.LogOut color="danger" />} variant="danger" title="Log out" />
          }
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ClientProfilePopover;
